/**
 * Location list — the venue-catalog index, mirroring event-list.view.ts's
 * poster-tile grid: each card's hero is a Google Static Maps image of the
 * venue in place of a poster, so the eye can place a venue before opening
 * it. A room with no `geo` (legitimately unmapped — "Saal 1" inside its
 * parent venue) renders a tasteful non-map hero instead of a broken map
 * tile. The create action rides the trailing end of the count line
 *a card click opens `location-form` in edit
 * mode via `show()`, the same overlay the count-line action opens in
 * create mode. Loading/error/denied/empty/populated mirrors
 * event-list.view.ts's own five-frame state machine.
 */
import { fill, gravity, overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { html, type TemplateResult } from "lit";
import { choose } from "lit/directives/choose.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { canManageEvents } from "../events/events.state.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import { workspaceMapsApiKey } from "../workspace/workspace.state.ts";
import "./location-form.view.ts";
import {
	locationMapMarkerColor,
	locationsListState,
	locationsPageView,
} from "./locations.state.ts";

@schmancyElement("location-list")
export class LocationList extends LocalizeMixin(SchmancyElement) {
	private openForm(id?: string): void {
		if (!canManageEvents.get()) return;
		show(() => html`<location-form .id=${id ?? ""}></location-form>`, {
			ariaLabel: id
				? this.t("admin.locations.form.overlayEdit")
				: this.t("admin.locations.form.overlayNew"),
		})
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	override render(): TemplateResult {
		const { rows } = locationsPageView.get();
		const canManage = canManageEvents.get();
		const markerColor = locationMapMarkerColor.get();

		return html`
			<section ${fill()} class="flex flex-col">
				<schmancy-surface
					${gravity({ distance: 18, mass: 1.1 })}
					type="container"
					class="block rounded-b-2xl px-3 py-3 md:px-6 md:py-5"
				>
					<div class="flex items-center justify-between gap-3">
						<div class="flex items-baseline gap-2 flex-wrap flex-1 min-w-0">
							<schmancy-typography type="display" token="" class="tabular-nums text-2xl md:text-4xl">
								${rows.length}
							</schmancy-typography>
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${this.t(
									rows.length === 1
										? "admin.locations.list.countSingular"
										: "admin.locations.list.countPlural",
								)}
							</schmancy-typography>
						</div>
						${when(
							canManage,
							() => html`
								<schmancy-icon-button icon="add" aria-label=${this.t("admin.locations.list.addAriaLabel")} @click=${() => this.openForm()}></schmancy-icon-button>
							`,
						)}
					</div>
				</schmancy-surface>

				<div ${overflowWithin()} class="min-h-0">
					${choose(locationsListState.get(), [
						[
							"loading",
							() => html`
								<div
									role="status"
									aria-busy="true"
									aria-label=${this.t("admin.locations.list.loadingAriaLabel")}
									class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6 px-4 md:px-6 pb-6 pt-4"
								>
									${repeat(
										[0, 1, 2, 3, 4] as const,
										(i) => i,
										() => html`
											<div class="flex flex-col gap-2">
												<schmancy-skeleton class="aspect-[4/3] w-full rounded-2xl"></schmancy-skeleton>
												<schmancy-skeleton shape="text" class="w-3/4"></schmancy-skeleton>
												<schmancy-skeleton shape="text" class="w-1/2"></schmancy-skeleton>
											</div>
										`,
									)}
								</div>
							`,
						],
						[
							"error",
							() => html`
								<div class="flex flex-col items-start gap-3 px-6 py-8">
									<schmancy-icon class="text-3xl text-error-default">error</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.locations.list.errorTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t("admin.locations.list.errorBody")}
									</schmancy-typography>
								</div>
							`,
						],
						[
							"denied",
							() => html`
								<div class="flex flex-col items-start gap-3 px-6 py-8">
									<schmancy-icon class="text-3xl text-error-default">lock</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.locations.list.deniedTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t("admin.locations.list.deniedBody")}
									</schmancy-typography>
								</div>
							`,
						],
						[
							"empty",
							() => html`
								<div class="flex flex-col items-start gap-3 px-6 py-8">
									<schmancy-icon class="text-3xl text-surface-onVariant">location_on</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.locations.list.emptyTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t("admin.locations.list.emptyBody")}
									</schmancy-typography>
									${when(
										canManage,
										() => html`
											<schmancy-button variant="filled" @click=${() => this.openForm()}>
												<schmancy-icon slot="prefix" aria-hidden="true">add</schmancy-icon>
												${this.t("admin.locations.list.addFirst")}
											</schmancy-button>
										`,
									)}
								</div>
							`,
						],
						[
							"populated",
							() => html`
								<div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6 px-4 md:px-6 pb-6 pt-4">
									${repeat(
										rows,
										(r) => r.id,
										(r) => {
											// A room with no pin of its own is physically at its
											// mapped ancestor's coordinates (locations.state.ts's
											// effectiveGeo) — the hero and the maps link ride that,
											// not the row's own (possibly absent) geo.
											const geo = r.effectiveGeo;
											const isInheritedGeo = !r.geo && !!geo;
											// Precomputed outside the `when()` closures below so neither
											// needs to re-narrow `geo` past a function boundary.
											// Static Maps refuses a keyless request, so an organization with no
											// key renders the placeholder rather than a broken image.
											const mapsApiKey = workspaceMapsApiKey.get();
											const mapImageUrl =
												geo && mapsApiKey
													? `https://maps.googleapis.com/maps/api/staticmap?center=${geo.lat},${geo.lng}&zoom=15&size=600x400&scale=2&markers=color:0x${markerColor}|${geo.lat},${geo.lng}&key=${mapsApiKey}`
													: null;
											const mapsSearchUrl = geo
												? `https://www.google.com/maps/search/?api=1&query=${geo.lat},${geo.lng}`
												: null;
											return html`
												<schmancy-card
													interactive
													class="group overflow-hidden rounded-2xl"
													aria-label=${this.t("admin.locations.list.detailsAriaLabel", { name: r.name })}
													@click=${() => this.openForm(r.id)}
												>
													<div class="flex flex-col gap-3">
														<div class="relative aspect-[4/3] w-full bg-surface-containerHigh">
															${when(
																mapImageUrl,
																(url) => html`
																	<img
																		src=${url}
																		alt=""
																		class="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
																	/>
																	${when(
																		isInheritedGeo,
																		() => html`
																			<schmancy-surface
																				type="container"
																				opacity="solid"
																				elevated
																				class="absolute bottom-2 left-2 z-10 rounded-full px-2.5 py-1"
																			>
																				<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
																					${this.t("admin.locations.common.withinPrefix", { name: r.geoInheritedFromName ?? "" })}
																				</schmancy-typography>
																			</schmancy-surface>
																		`,
																	)}
																`,
																() => html`
																	<div class="flex h-full w-full flex-col items-center justify-center gap-1 px-4 text-center">
																		<schmancy-icon class="text-5xl text-surface-onVariant/40">
																			${r.withinName ? "meeting_room" : "location_on"}
																		</schmancy-icon>
																		${when(
																			r.withinName,
																			() => html`
																				<schmancy-typography type="body" token="xs" class="text-surface-onVariant/60">
																					${this.t("admin.locations.common.withinPrefix", { name: r.withinName ?? "" })}
																				</schmancy-typography>
																			`,
																		)}
																	</div>
																`,
															)}
															${when(
																mapsSearchUrl,
																(searchUrl) => html`
																	<schmancy-surface
																		type="container"
																		opacity="solid"
																		elevated
																		class="absolute right-2 top-2 z-10 rounded-full"
																	>
																		<a
																			href=${searchUrl}
																			target="_blank"
																			rel="noopener"
																			aria-label=${this.t("admin.locations.list.openInMapsAriaLabel", { name: r.name })}
																			class="flex items-center justify-center rounded-full p-1.5 hover:bg-surface-container"
																			@click=${(e: Event) => e.stopPropagation()}
																		>
																			<schmancy-icon class="text-lg text-surface-onVariant">open_in_new</schmancy-icon>
																		</a>
																	</schmancy-surface>
																`,
															)}
														</div>
														<div class="flex flex-col gap-1 px-3 pb-3">
															<schmancy-typography type="title" token="sm" class="truncate">${r.name}</schmancy-typography>
															<schmancy-typography type="body" token="sm" class="text-surface-onVariant truncate">
																${r.address}
															</schmancy-typography>
															${when(
																// Already conveyed once, in the hero: the map-overlay
																// caption when the pin is inherited, the icon caption
																// when there's no pin anywhere. This context line earns its
																// keep only for the remaining case — a room with its
																// own pin that's still nested under a parent.
																r.withinName && r.geo,
																() => html`
																	<schmancy-typography type="label" token="md" class="text-surface-onVariant self-start">
																		${this.t("admin.locations.common.withinPrefix", { name: r.withinName ?? "" })}
																	</schmancy-typography>
																`,
															)}
															<schmancy-typography type="body" token="xs" class="text-surface-onVariant/70 truncate">
																${
																	r.usedByCount > 0
																		? this.t(
																				r.usedByCount === 1
																					? "admin.locations.list.usedByOne"
																					: "admin.locations.list.usedByOther",
																				{ count: r.usedByCount },
																			)
																		: this.t("admin.locations.list.unused")
																}
															</schmancy-typography>
														</div>
													</div>
												</schmancy-card>
											`;
										},
									)}
								</div>
							`,
						],
					])}
				</div>
			</section>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"location-list": LocationList;
	}
}
